/**
 * How the result strip reads a failed Azure Storage test, from main's STRUCTURED errors (`StorageFailureContext`:
 * category, code, fix kind — never the sentence):
 * - a REFUSAL of the settings themselves — `invalid_input` (the key-access checks: a resource ID naming another account,
 *   an endpoint the key is not bound to; a missing account name) or `unsupported` (a sovereign cloud for key access) —
 *   withholds Save anyway (C2 review C2-02): saving would store what the app refuses; an ordinary unsuccessful probe
 *   (unreachable, signed-in but refused, a missing role) keeps it;
 * - the field the failure is about (C2 review C2-05): the credential for a sign-in failure, the resource ID for the
 *   key-access account checks, the endpoint override or the account for an endpoint refusal, the account otherwise.
 */
import type { Connection } from '@/types/electron';
import type { StorageFailureContext } from '../../utils/storageTestLegs';

const REFUSALS: ReadonlySet<string> = new Set(['invalid_input', 'unsupported']);

/** The first failure that refuses the settings, if any. */
function refusalOf(failures: readonly StorageFailureContext[] | undefined): StorageFailureContext | undefined {
  return failures?.find((failure) => REFUSALS.has(failure.category));
}

export function storageSaveAnywayRefusal(failures: readonly StorageFailureContext[] | undefined): string | undefined {
  const refusal = refusalOf(failures);
  if (!refusal) return undefined;
  switch (refusal.code) {
    case 'KeyAccess:resource-id-mismatch':
      return 'Save anyway is not offered while the storage account resource ID names another account: correct or clear it, then test again.';
    case 'KeyAccess:endpoint-mismatch':
    case 'KeyAccess:unsupported-cloud':
      return "Save anyway is not offered for an endpoint the account key is not read for: use the account's own public endpoints, or sign in with the account key or a SAS, then test again.";
    default:
      return 'Save anyway is not offered for a setting Azure Storage refuses: correct it, then test again.';
  }
}

/** The endpoint override the refusal is about, when the connection has one for that service. */
function endpointField(failure: StorageFailureContext, connection: Connection | undefined): string {
  const endpoints = connection?.azureStorageEndpoints ?? {};
  const service = failure.service && failure.service in endpoints ? failure.service : (Object.keys(endpoints)[0] as string | undefined);
  return service && !connection?.azureStorageEmulator ? `[data-testid="storage-endpoint-${service}"]` : '#server';
}

function credentialField(connection: Connection | undefined): string | undefined {
  switch (connection?.authentication) {
    case 'Account Key':
    case 'Shared Access Signature': return '#password';
    case 'Service Principal': return '#login';
    case 'Anonymous': return '#server';
    default: return '#authentication';
  }
}

export function storageFieldSelector(failures: readonly StorageFailureContext[] | undefined, connection: Connection | undefined): string | undefined {
  const failure = refusalOf(failures) ?? failures?.[0];
  if (!failure) return undefined;
  switch (failure.code) {
    case 'KeyAccess:resource-id-mismatch':
    case 'KeyAccess:account-ambiguous':
    case 'KeyAccess:account-not-found':
      return '#storage-resource-id';
    case 'KeyAccess:endpoint-mismatch':
    case 'KeyAccess:unsupported-cloud':
      return endpointField(failure, connection);
    case 'KeyAccess:shared-key-disabled':
    case 'KeyAccess:list-keys-denied':
    case 'KeyAccess:arm-read-denied':
      return '#storage-data-access-select';
    default:
      break;
  }
  switch (failure.category) {
    case 'authentication': return credentialField(connection);
    // A SAS or key that is refused names the credential; an Entra identity's missing role is not in this form.
    case 'authorization': return connection?.authentication === 'Account Key' || connection?.authentication === 'Shared Access Signature' ? '#password' : undefined;
    case 'network':
    case 'not_found':
    case 'timeout':
    case 'invalid_input':
    case 'unsupported':
      return '#server';
    default:
      return undefined;
  }
}
